import { useState, useEffect, useRef, useCallback } from "react";
import {
  FiX, FiChevronLeft, FiChevronRight, FiTrash2,
  FiEye, FiSend, FiVolume2, FiVolumeX, FiMusic,
} from "react-icons/fi";
import api from "../../utils/api";
import useAuthStore from "../../store/authStore";
import { useSocket } from "../../context/SocketContext";
import { getInitials } from "../../utils/helpers";
import styles from "./StatusViewer.module.css";

const DURATION = 5000;
const QUICK_EMOJIS = ["❤️", "😂", "😮", "😢", "👏"];

const StatusViewer = ({ statusGroups, startIndex = 0, onClose, onDeleted }) => {
  const { user }  = useAuthStore();
  const socketRef = useSocket();

  const [groupIndex,   setGroupIndex]   = useState(startIndex);
  const [statusIndex,  setStatusIndex]  = useState(0);
  const [progress,     setProgress]     = useState(0);
  const [paused,       setPaused]       = useState(false);
  const [showViews,    setShowViews]    = useState(false);
  const [reply,        setReply]        = useState("");
  const [muted,        setMuted]        = useState(false);
  const [views,        setViews]        = useState([]);
  const [replySent,    setReplySent]    = useState(false);
  const [myReaction,   setMyReaction]   = useState(null);
  const [reactionSent, setReactionSent] = useState(false);
  const [liveViewCount, setLiveViewCount] = useState(0);
  const [hasNewReaction, setHasNewReaction] = useState(false); // green dot on new/changed reaction

  const timerRef    = useRef(null);
  const startTime   = useRef(null);
  const elapsed     = useRef(0);
  const progressRef = useRef(0);
  const audioRef    = useRef(null);
  const videoRef    = useRef(null);
  const viewsPanelRef = useRef(null);

  const group   = statusGroups[groupIndex];
  const current = group?.statuses[statusIndex];
  const isOwn   = group?.user._id === user._id;

  // Song
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (current?.songUrl) {
      if (audio.src !== current.songUrl) { audio.src = current.songUrl; audio.load(); }
      if (!paused && !showViews) audio.play().catch(() => {});
      else audio.pause();
    } else { audio.pause(); audio.src = ""; }
  }, [current?.songUrl, paused, showViews]);

  useEffect(() => () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; }
  }, []);

  // Mark viewed
  useEffect(() => {
    if (!current?._id || isOwn) return;
    api.post("/status/" + current._id + "/view").catch(() => {});
  }, [current?._id]);

  // Fetch views when panel opens
  useEffect(() => {
    if (!showViews || !isOwn || !current?._id) return;
    api.get("/status").then((res) => {
      const myGroup = res.data.statuses?.find((g) => g.user._id === user._id);
      const s = myGroup?.statuses?.find((s) => s._id === current._id);
      if (s) {
        // merge reactions into views
        const viewsWithReactions = (s.views || []).map((v) => {
          const r = (s.reactions || []).find(
            (rx) => rx.user?._id === v.user?._id || rx.user === v.user?._id
          );
          return { ...v, reaction: r?.emoji || null };
        });
        setViews(viewsWithReactions);
      }
    }).catch(() => {});
  }, [showViews, current?._id]);

  // Sync liveViewCount from current status prop
  useEffect(() => {
    setLiveViewCount(current?.views?.length || 0);
  }, [current?._id]);

  // Live socket updates for views panel
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket || !isOwn) return;
    const onViewed = ({ statusId }) => {
      if (current?._id === statusId) {
        setLiveViewCount((c) => c + 1);
      }
      if (showViews && current?._id === statusId) {
        api.get("/status").then((res) => {
          const myGroup = res.data.statuses?.find((g) => g.user._id === user._id);
          const s = myGroup?.statuses?.find((s) => s._id === current._id);
          if (s) {
            const viewsWithReactions = (s.views || []).map((v) => {
              const r = (s.reactions || []).find(
                (rx) => rx.user?._id === v.user?._id || rx.user === v.user?._id
              );
              return { ...v, reaction: r?.emoji || null };
            });
            setViews(viewsWithReactions);
          }
        }).catch(() => {});
      }
    };
    const onReaction = ({ statusId, reactor, emoji }) => {
      if (current?._id === statusId) {
        setHasNewReaction(true); // green dot again
        setViews((prev) => prev.map((v) =>
          (v.user?._id === reactor._id || v.user === reactor._id)
            ? { ...v, reaction: emoji }
            : v
        ));
      }
    };
    socket.on("status:viewed",   onViewed);
    socket.on("status:reaction", onReaction);
    return () => {
      socket.off("status:viewed",   onViewed);
      socket.off("status:reaction", onReaction);
    };
  }, [socketRef?.current, isOwn, showViews, current?._id]);

  // Restore my existing reaction when status changes
  useEffect(() => {
    if (!current || isOwn) return;
    const mine = current.reactions?.find(
      (r) => r.user?._id === user._id || r.user === user._id
    );
    setMyReaction(mine?.emoji || null);
    setReactionSent(false);
  }, [current?._id]);

  // Navigation
  const goNext = useCallback(() => {
    const g = statusGroups[groupIndex];
    if (statusIndex < g.statuses.length - 1) {
      setStatusIndex((i) => i + 1);
    } else if (groupIndex < statusGroups.length - 1) {
      setGroupIndex((i) => i + 1);
      setStatusIndex(0);
    } else { onClose(); return; }
    setProgress(0); progressRef.current = 0; elapsed.current = 0;
  }, [groupIndex, statusIndex, statusGroups, onClose]);

  const goPrev = useCallback(() => {
    if (statusIndex > 0) setStatusIndex((i) => i - 1);
    else if (groupIndex > 0) { setGroupIndex((i) => i - 1); setStatusIndex(0); }
    setProgress(0); progressRef.current = 0; elapsed.current = 0;
  }, [groupIndex, statusIndex]);

  // Progress timer (text/image)
  useEffect(() => {
    if (!current || current.mediaType === "video" || paused || showViews) {
      cancelAnimationFrame(timerRef.current);
      startTime.current = null;
      return;
    }
    const tick = () => {
      const now = Date.now();
      if (!startTime.current) startTime.current = now;
      elapsed.current = Math.min(elapsed.current + (now - startTime.current), DURATION);
      startTime.current = now;
      const pct = (elapsed.current / DURATION) * 100;
      progressRef.current = pct;
      setProgress(pct);
      if (pct >= 100) { cancelAnimationFrame(timerRef.current); goNext(); return; }
      timerRef.current = requestAnimationFrame(tick);
    };
    timerRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(timerRef.current);
  }, [current?._id, paused, showViews, goNext]);

  // Video progress via timeupdate
  const handleVideoTimeUpdate = () => {
    const vid = videoRef.current;
    if (!vid || !vid.duration) return;
    setProgress((vid.currentTime / vid.duration) * 100);
  };

  // Reset on status change
  useEffect(() => {
    elapsed.current = 0; startTime.current = null; progressRef.current = 0;
    setProgress(0); setPaused(false); setShowViews(false);
    setViews([]); setReply(""); setReplySent(false); setHasNewReaction(false);
  }, [groupIndex, statusIndex]);

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete("/status/" + current._id);
      socketRef?.current?.emit("status:deleted", { statusId: current._id });
      onDeleted?.(current._id);
      goNext();
    } catch { }
  };

  // Reply
  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    try {
      const res = await api.post("/conversations", { userId: group.user._id });
      const convId = res.data.conversation._id;
      const statusReply = {
        mediaType:   current.mediaType,
        media:       current.media || "",
        textContent: current.textContent || "",
        bgColor:     current.bgColor || "#7b7ff5",
        userName:    group.user.name,
      };
      await api.post("/messages", { conversationId: convId, text: reply.trim(), statusReply });
      setReply(""); setReplySent(true);
      setTimeout(() => setReplySent(false), 2000);
    } catch { }
  };

  // React with emoji
  const handleReact = async (emoji) => {
    const newEmoji = myReaction === emoji ? null : emoji;
    setMyReaction(newEmoji);
    if (!newEmoji) return;
    setReactionSent(true);
    setTimeout(() => setReactionSent(false), 1500);
    try {
      await api.post("/status/" + current._id + "/react", { emoji: newEmoji });
    } catch { }
  };

  const timeAgo = (date) => {
    const diff = Date.now() - new Date(date);
    const hrs  = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hrs > 0) return hrs + "h ago";
    if (mins > 0) return mins + "m ago";
    return "Just now";
  };

  if (!group || !current) return null;

  return (
    <div
      className={styles.overlay}
      onClick={() => { if (showViews) setShowViews(false); }}
      onMouseDown={(e) => {
        if (showViews) return;
        if (e.target.closest("button") || e.target.closest("form") || (viewsPanelRef.current && viewsPanelRef.current.contains(e.target))) return;
        setPaused(true);
      }}
      onMouseUp={() => setPaused(false)}
      onTouchStart={(e) => {
        if (showViews) return;
        if (e.target.closest("button") || e.target.closest("form")) return;
        setPaused(true);
      }}
      onTouchEnd={() => setPaused(false)}
    >
      {/* Progress bars */}
      <div className={styles.progressBars}>
        {group.statuses.map((_, i) => (
          <div key={i} className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{
                width: i < statusIndex ? "100%" : i === statusIndex ? progress + "%" : "0%",
                transition: i === statusIndex ? "none" : undefined,
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className={styles.header} onClick={(e) => e.stopPropagation()}>
        <div className={styles.userInfo}>
          {group.user.avatar
            ? <img src={group.user.avatar} alt="" className={styles.avatar} />
            : <div className={styles.avatarFallback}>{getInitials(group.user.name)}</div>
          }
          <div>
            <div className={styles.userName}>{isOwn ? "My Status" : group.user.name}</div>
            <div className={styles.timeInfo}>{timeAgo(current.createdAt)}</div>
          </div>
        </div>
        <div className={styles.headerActions}>
          {current.mediaType === "video" && (
            <button className={styles.iconBtn} onClick={() => setMuted((m) => !m)}>
              {muted ? <FiVolumeX size={20} /> : <FiVolume2 size={20} />}
            </button>
          )}
          {isOwn && (
            <>
              <button className={styles.iconBtn} onClick={() => { setShowViews((v) => !v); setHasNewReaction(false); }}>
                <FiEye size={20} />
                {liveViewCount > 0 && (
                  <span className={styles.viewCountBubble}>{liveViewCount}</span>
                )}
                {/* Green dot — shows when nobody viewed yet OR when someone newly reacted */}
                {(liveViewCount === 0 || hasNewReaction) && (
                  <span className={styles.eyeGreenDot} />
                )}
              </button>
              <button className={styles.iconBtn} onClick={handleDelete}>
                <FiTrash2 size={18} />
              </button>
            </>
          )}
          <button className={styles.closeBtn} onClick={(e) => { e.stopPropagation(); onClose(); }}>
            <FiX size={22} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content} onClick={(e) => e.stopPropagation()}>
        {current.mediaType === "image" && (
          <img src={current.media} alt="status" className={styles.statusImage} />
        )}
        {current.mediaType === "video" && (
          <video
            ref={videoRef}
            src={current.media}
            className={styles.statusVideo}
            autoPlay
            muted={muted}
            onEnded={goNext}
            onTimeUpdate={handleVideoTimeUpdate}
          />
        )}
        {current.mediaType === "text" && (
          <div className={styles.textStatus} style={{ background: current.bgColor }}>
            <p className={styles.textContent}>{current.textContent}</p>
          </div>
        )}
        <audio ref={audioRef} loop style={{ display: "none" }} />
        {current?.songUrl && (
          <div className={styles.songPill} onClick={(e) => e.stopPropagation()}>
            <FiMusic size={12} />
            <span className={styles.songPillTitle}>{current.songTitle || "Music"}</span>
            <div className={styles.songNoteAnim + (paused ? " " + styles.songPaused : "")}>
              <span /><span /><span />
            </div>
          </div>
        )}
        {current.caption && <div className={styles.caption}>{current.caption}</div>}
      </div>

      {/* Tap zones */}
      {!showViews && (
        <>
          <div className={styles.tapLeft}  onClick={(e) => { e.stopPropagation(); goPrev(); }} />
          <div className={styles.tapRight} onClick={(e) => { e.stopPropagation(); goNext(); }} />
        </>
      )}

      {/* Nav arrows */}
      {!showViews && groupIndex > 0 && (
        <button className={styles.navLeft} onClick={(e) => { e.stopPropagation(); goPrev(); }}>
          <FiChevronLeft size={28} />
        </button>
      )}
      {!showViews && groupIndex < statusGroups.length - 1 && (
        <button className={styles.navRight} onClick={(e) => { e.stopPropagation(); goNext(); }}>
          <FiChevronRight size={28} />
        </button>
      )}

      {/* Views panel */}
      {showViews && isOwn && (
        <>
          <div className={styles.viewsBackdrop} onClick={(e) => { e.stopPropagation(); setShowViews(false); }} />
          <div ref={viewsPanelRef} className={styles.viewsPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.viewsHandle} onClick={() => setShowViews(false)} />
            <div className={styles.viewsTitle}>
              <FiEye size={16} />
              Viewed by
              <span className={styles.viewsCount}>{views.length}</span>
            </div>
            {views.length === 0 ? (
              <p className={styles.noViews}>No one has viewed this yet</p>
            ) : (
              views.map((v, i) => (
                <div key={i} className={styles.viewerItem}>
                  <div className={styles.viewerAvatarWrap}>
                    {v.user?.avatar
                      ? <img src={v.user.avatar} alt="" className={styles.viewerAvatar} />
                      : <div className={styles.viewerAvatarFallback}>{getInitials(v.user?.name || "?")}</div>
                    }
                    {v.reaction && (
                      <span className={styles.viewerReactionBadge}>{v.reaction}</span>
                    )}
                  </div>
                  <span className={styles.viewerName}>{v.user?.name || "Unknown"}</span>
                  <span className={styles.viewerTime}>{timeAgo(v.viewedAt)}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Emoji reactions + reply bar */}
      {!isOwn && (
        <div className={styles.replyArea} onClick={(e) => e.stopPropagation()}>
          <div className={styles.emojiBar}>
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                className={styles.emojiBtn + (myReaction === emoji ? " " + styles.emojiBtnActive : "")}
                onClick={() => handleReact(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
          <form className={styles.replyBox} onSubmit={handleReply}>
            <input
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={
                reactionSent ? (myReaction + " Reaction sent!") :
                replySent    ? "Sent! ✓" :
                ("Reply to " + group.user.name + "…")
              }
              className={styles.replyInput + (replySent || reactionSent ? " " + styles.replySent : "")}
            />
            <button type="submit" className={styles.replyBtn} disabled={!reply.trim()}>
              <FiSend size={18} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default StatusViewer;
